export type Locale = 'en' | 'fa' | 'tr' | 'ar';

export interface Dictionary {
  dir: 'ltr' | 'rtl';
  name: string;
  nativeName: string;
  flag: string;
  nav: {
    articles: string;
    volunteerForm: string;
    contactUs: string;
    login: string;
    logout: string;
    admin: string;
    home: string;
  };
  hero: {
    badge: string;
    title: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    stats: {
      participation: string;
      participationSub: string;
      hours: string;
      hoursSub: string;
      volunteers: string;
      volunteersSub: string;
    };
    aboutTitle: string;
    aboutQuote: string;
    aboutDesc: string;
    impactTitle: string;
    impactDesc: string;
    valuesTitle: string;
    valuesDesc: string;
    pillar1Title: string;
    pillar1Desc: string;
    pillar2Title: string;
    pillar2Desc: string;
    pillar3Title: string;
    pillar3Desc: string;
    branchesBadge: string;
    branchesTitle: string;
    branchesDesc: string;
    chapterMainName: string;
    chapterMainRole: string;
    chapterTehranName: string;
    chapterKarajName: string;
    chapterMashhadName: string;
    chapterTabrizName: string;
    chapterRoleActive: string;
    chapterIntName: string;
    chapterIntRole: string;
    joinTitle: string;
    joinDesc: string;
    joinCityPlaceholder: string;
    joinFooterNotice: string;
  };
  footer: {
    greetingMorning: string;
    greetingAfternoon: string;
    greetingEvening: string;
    description: string;
    quickLinks: string;
    newsletterTitle: string;
    newsletterDesc: string;
    emailPlaceholder: string;
    subscribeBtn: string;
    copyright: string;
    brandName: string;
    joinCommunity: string;
    engineeredBy: string;
    samanic: string;
  };
  contactUs: {
    title: string;
    subtitle: string;
    formTitle: string;
    nameLabel: string;
    namePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    msgLabel: string;
    msgPlaceholder: string;
    sendBtn: string;
    sending: string;
    successMsg: string;
    errorMsg: string;
    socialTitle: string;
  };
  articles: {
    title: string;
    subtitle: string;
    searchPlaceholder: string;
    readMore: string;
    minRead: string;
    noArticles: string;
    noBookmarks: string;
    author: string;
    publishedOn: string;
    listenVoice: string;
    playingVoice: string;
    voiceGuidance: string;
    commentsTitle: string;
    writeComment: string;
    submitComment: string;
    commentPendingNotice: string;
    like: string;
    bookmark: string;
    saved: string;
    share: string;
    copied: string;
    allTab: string;
    bookmarksTab: string;
    clearFilters: string;
    anonymous: string;
    noCommentsYet: string;
    beFirstComment: string;
    editorialTeam: string;
    langFilterAll: string;
    editArticle: string;
    deleteArticle: string;
    confirmDeleteTitle: string;
    confirmDeleteMsg: string;
    deleteSuccess: string;
    writtenIn: string;
  };
  createArticle: {
    pageTitle: string;
    titleLabel: string;
    titlePlaceholder: string;
    authorLabel: string;
    authorPlaceholder: string;
    contentLabel: string;
    coverUploadLabel: string;
    voiceUploadLabel: string;
    voiceExplanation: string;
    submitBtn: string;
    publishing: string;
    editMode: string;
    previewMode: string;
    startRecord: string;
    stopRecord: string;
    uploadMp3: string;
    removeAudio: string;
    audioReady: string;
    playingAudio: string;
    livePreviewBanner: string;
    untitled: string;
    orText: string;
    micDeniedTitle: string;
    micDeniedDesc: string;
    audioAttachedTitle: string;
    audioAttachedDesc: string;
    uploadErrorTitle: string;
    uploadErrorDesc: string;
    missingDataTitle: string;
    missingDataDesc: string;
    publishedTitle: string;
    publishedDesc: string;
    publishingFailedTitle: string;
    articleLanguage: string;
    selectLanguageDesc: string;
    editTitle: string;
    updateBtn: string;
    updatedTitle: string;
    updatedDesc: string;
  };
  volunteer: {
    title: string;
    subtitle: string;
    nameLabel: string;
    countryLabel: string;
    cityLabel: string;
    selectCountry: string;
    selectCity: string;
    platformLabel: string;
    contactIdPlaceholder: string;
    platformSelectGuide: string;
    submitBtn: string;
    successTitle: string;
    successMsg: string;
    stepGeneral: string;
    stepExperience: string;
    stepMotivation: string;
    incompleteFormTitle: string;
    incompleteFormDesc: string;
  };
  notFound: {
    title: string;
    subtitle: string;
    desc: string;
    homeBtn: string;
  };
  admin: {
    title: string;
    subtitle: string;
    newArticleBtn: string;
    totalViews: string;
    totalArticles: string;
    pendingComments: string;
    topAuthor: string;
    mostViewed: string;
    moderateComments: string;
    volunteersList: string;
    approve: string;
    delete: string;
    noData: string;
    unknownAuthor: string;
    pendingTag: string;
    onArticle: string;
    platform: string;
    registeredOn: string;
    realViewCounter: string;
    commentApprovedTitle: string;
    commentApprovedDesc: string;
    deleteConfirm: string;
    commentDeletedTitle: string;
    commentDeletedDesc: string;
  };
  login: {
    title: string;
    subtitle: string;
    emailLabel: string;
    passwordLabel: string;
    submitBtn: string;
    checking: string;
    errorMsg: string;
    capsLockAlert: string;
  };
  common: {
    back: string;
    loading: string;
    save: string;
    saved: string;
    error: string;
    success: string;
  };
}

export const dictionaries: Record<Locale, Dictionary> = {
  en: {
    dir: 'ltr',
    name: 'English',
    nativeName: 'English (US)',
    flag: '🇺🇸',
    nav: {
      articles: 'Articles',
      volunteerForm: 'Volunteer Form',
      contactUs: 'Contact Us',
      login: 'Sign In',
      logout: 'Sign Out',
      admin: 'Admin Panel',
      home: 'Home',
    },
    hero: {
      badge: 'About Us | The Story of Green Campaign',
      title: 'Green Campaign: From an Idea to a Regional Movement for a Clean Earth',
      subtitle: 'We are a volunteer-driven environmental network with a mission that transcends geographic borders. Without complex hierarchies, we empower passionate individuals to champion cleanliness in their own cities.',
      ctaPrimary: 'Become a City Representative',
      ctaSecondary: 'Explore Active Hubs',
      stats: {
        participation: 'Participation',
        participationSub: 'People involved',
        hours: 'Volunteered Hours',
        hoursSub: 'Across active hubs',
        volunteers: 'Active Hubs',
        volunteersSub: 'Cities across region',
      },
      aboutTitle: 'The Birth of an Idea',
      aboutQuote: 'Green Campaign started with a simple question: “Why wait for others to clean up our cities?”',
      aboutDesc: 'We were a passionate team of volunteers exhausted by seeing litter in urban spaces and nature. Rather than complaining, we took field action. We quickly realized this need transcends geographic borders—it is a shared concern for a sustainable planet.',
      impactTitle: 'Cross-Border Impact',
      impactDesc: 'Uniting communities worldwide for a cleaner, resilient planet.',
      valuesTitle: 'How We Operate & Our Red Line',
      valuesDesc: 'We reject complex centralized bureaucracy. We believe each community knows its local solutions best.',
      pillar1Title: '1. Verified Photos',
      pillar1Desc: 'Every clean-up drive is documented with visual proof of active volunteer work.',
      pillar2Title: '2. Exact Waste Weight',
      pillar2Desc: 'We measure and record the precise weight in kilograms of collected environmental waste.',
      pillar3Title: '3. Volunteer Count',
      pillar3Desc: 'Transparent metrics showing real-time social participation and community empowerment.',
      branchesBadge: 'Current Standing & Growth',
      branchesTitle: '5 Active Hubs Leading the Movement',
      branchesDesc: 'Our journey has just begun, but we have established solid foundations. Currently, our network operates through 5 active core regional chapters, expanding rapidly across communities.',
      chapterMainName: 'Main Campaign Chapter',
      chapterMainRole: 'Central Hub',
      chapterTehranName: 'Tehran Chapter',
      chapterKarajName: 'Karaj Chapter',
      chapterMashhadName: 'Mashhad Chapter',
      chapterTabrizName: 'Tabriz Chapter',
      chapterRoleActive: 'Active Chapter',
      chapterIntName: 'International Hub',
      chapterIntRole: 'Upcoming Expansion',
      joinTitle: 'Does Your City Need a Cleanliness Ambassador?',
      joinDesc: 'We reached this milestone because passionate individuals said, “I can make a difference.” To become a city representative, all you need is passion—no funding required.',
      joinCityPlaceholder: 'Your City / Region',
      joinFooterNotice: 'Let’s prove together that community resiliency knows no borders. 🌱',
    },
    footer: {
      greetingMorning: 'Good Morning, Protector of the Earth!',
      greetingAfternoon: 'Good Afternoon, Champion of Nature!',
      greetingEvening: 'Good Evening, Friend of the Planet!',
      description: 'An international citizen-led environmental movement dedicated to a clean earth, sustainable lifestyles, and preserving nature across borders.',
      quickLinks: 'Quick Navigation',
      newsletterTitle: 'Stay Connected & Updated',
      newsletterDesc: 'Join thousands of environmental activists receiving bi-weekly dispatch reports from active frontline campaigns.',
      emailPlaceholder: 'Enter your email address...',
      subscribeBtn: 'Join Dispatch',
      copyright: 'All rights reserved to Green Campaign.',
      brandName: 'Green Campaign',
      joinCommunity: 'Join the Community',
      engineeredBy: 'Engineered by',
      samanic: 'Samanic',
    },
    contactUs: {
      title: 'Get in Touch with Green Campaign',
      subtitle: 'Have proposals, feedback, or media inquiries? We welcome direct collaboration from activists worldwide.',
      formTitle: 'Send a Direct Message',
      nameLabel: 'Your Full Name',
      namePlaceholder: 'John Doe',
      emailLabel: 'Email Address',
      emailPlaceholder: 'hello@example.com',
      msgLabel: 'Your Message or Proposal',
      msgPlaceholder: 'How would you like to contribute or collaborate with us?...',
      sendBtn: 'Send Message',
      sending: 'Transmitting...',
      successMsg: 'Your message has been safely delivered to our global editorial team!',
      errorMsg: 'Transmission failed. Please check your network connection and try again.',
      socialTitle: 'Follow Our Regional Dispatches',
    },
    articles: {
      title: 'Environmental Dispatch & Articles',
      subtitle: 'Latest ground updates, field investigations, educational guides, and inspiring community narratives.',
      searchPlaceholder: 'Search articles by keywords or title...',
      readMore: 'Read Full Investigation',
      minRead: 'min read',
      noArticles: 'No matching articles found for your criteria!',
      noBookmarks: 'You have not saved any articles to your personal bookmarks yet.',
      author: 'Author',
      publishedOn: 'Published on',
      listenVoice: 'Listen to Author Narration',
      playingVoice: 'Playing Narration...',
      voiceGuidance: 'Audio feature available: You can listen to the writer read this article directly by tapping the header audio button!',
      commentsTitle: 'Community Discussions',
      writeComment: 'Share your perspective or constructive thought...',
      submitComment: 'Post Comment',
      commentPendingNotice: 'Your comment has been submitted and is currently queued for editorial moderation.',
      like: 'Like',
      bookmark: 'Bookmark',
      saved: 'Saved',
      share: 'Share Link',
      copied: 'Link Copied!',
      allTab: 'All Articles',
      bookmarksTab: 'Saved Bookmarks',
      clearFilters: 'Clear Filters',
      anonymous: 'Anonymous Member',
      noCommentsYet: 'No discussions started yet!',
      beFirstComment: 'Be the first environmental advocate to share your feedback below.',
      editorialTeam: 'Green Campaign Editorial',
      langFilterAll: 'All Languages',
      editArticle: 'Edit Article',
      deleteArticle: 'Delete Article',
      confirmDeleteTitle: 'Delete Article?',
      confirmDeleteMsg: 'Are you sure you want to delete this article? This action cannot be undone.',
      deleteSuccess: 'Article deleted successfully.',
      writtenIn: 'Written in',
    },
    createArticle: {
      pageTitle: 'Publish New Article',
      titleLabel: 'Article Title',
      titlePlaceholder: 'Enter a compelling environmental headline...',
      authorLabel: 'Author Name',
      authorPlaceholder: 'Author name or representative hub...',
      contentLabel: 'Article Body Content',
      coverUploadLabel: 'Cover Image',
      voiceUploadLabel: 'Author Voice Narration (Optional)',
      voiceExplanation: 'Record yourself reading this article or attach an MP3 audio file so readers can stream your voice directly in the header.',
      submitBtn: 'Publish Article',
      publishing: 'Publishing to Network...',
      editMode: 'Editor Mode',
      previewMode: 'Live Preview',
      startRecord: 'Start Mic Recording',
      stopRecord: 'Stop Recording',
      uploadMp3: 'Attach Audio MP3',
      removeAudio: 'Remove Audio',
      audioReady: 'Audio Narration Attached!',
      playingAudio: 'Playing Narration Preview...',
      livePreviewBanner: 'Live Pre-publication Reading View',
      untitled: 'Untitled Article Draft',
      orText: 'OR',
      micDeniedTitle: 'Microphone Access Denied',
      micDeniedDesc: 'Please enable audio permissions in your browser.',
      audioAttachedTitle: 'Audio Attached!',
      audioAttachedDesc: 'Your audio narration has been successfully linked to this article.',
      uploadErrorTitle: 'Upload Error',
      uploadErrorDesc: 'Failed to process audio file.',
      missingDataTitle: 'Missing Data',
      missingDataDesc: 'Please provide both title and content before publishing.',
      publishedTitle: 'Published!',
      publishedDesc: 'Your article was published successfully.',
      publishingFailedTitle: 'Publishing Failed',
      articleLanguage: 'Article Language',
      selectLanguageDesc: 'Select the language in which this article is written:',
      editTitle: 'Edit Article',
      updateBtn: 'Save & Update Article',
      updatedTitle: 'Updated Successfully!',
      updatedDesc: 'Your changes have been saved to the database.',
    },
    volunteer: {
      title: 'Join as a City Volunteer Representative',
      subtitle: 'Step forward and organize environmental stewardship and awareness in your municipality.',
      nameLabel: 'Full Name',
      countryLabel: 'Country of Residence',
      cityLabel: 'City or Municipality',
      selectCountry: 'Select Country',
      selectCity: 'Select City / District',
      platformLabel: 'Preferred Direct Contact Channel',
      contactIdPlaceholder: 'Enter your ID or phone number on',
      platformSelectGuide: 'Select your preferred communication messenger from the icons 👈',
      submitBtn: 'Submit Volunteer Application',
      successTitle: 'Welcome to the Movement!',
      successMsg: 'Your representative application has been logged into the network queue!',
      stepGeneral: 'General Information',
      stepExperience: 'Experience & Interests',
      stepMotivation: 'Motivation Statement',
      incompleteFormTitle: 'Incomplete Form',
      incompleteFormDesc: 'Please fill in all required fields before submitting your registration.',
    },
    notFound: {
      title: '404 - Page Not Found',
      subtitle: 'We ventured into unmapped territory',
      desc: 'The page or resource you are looking for has been moved, renamed, or no longer exists in our digital ecosystem.',
      homeBtn: 'Return to Homepage',
    },
    admin: {
      title: 'Executive Admin Panel',
      subtitle: 'Real-time database telemetry and global content moderation desk',
      newArticleBtn: 'Compose Article',
      totalViews: 'Total Article Views',
      totalArticles: 'Published Articles',
      pendingComments: 'Pending Comments',
      topAuthor: 'Top Author Hub',
      mostViewed: 'Most Viewed Highlight',
      moderateComments: 'Moderation Queue',
      volunteersList: 'Registered Volunteer Rosters',
      approve: 'Approve & Publish',
      delete: 'Delete Record',
      noData: 'No active items in database queue.',
      unknownAuthor: 'General Editorial',
      pendingTag: 'Pending Review',
      onArticle: 'On Article:',
      platform: 'Channel:',
      registeredOn: 'Registered:',
      realViewCounter: 'Real View Telemetry:',
      commentApprovedTitle: 'Comment Approved',
      commentApprovedDesc: 'Comment has been published publicly.',
      deleteConfirm: 'Are you sure you want to delete this comment?',
      commentDeletedTitle: 'Deleted',
      commentDeletedDesc: 'Comment removed from system.',
    },
    login: {
      title: 'Executive Portal Login',
      subtitle: 'Enter your secure credentials to access internal system administration',
      emailLabel: 'Admin Email Address',
      passwordLabel: 'Security Password',
      submitBtn: 'Authenticate & Enter',
      checking: 'Verifying Credentials...',
      errorMsg: 'Authentication rejected: Invalid email or password.',
      capsLockAlert: 'Warning: Caps Lock is active on your keyboard!',
    },
    common: {
      back: 'Go Back',
      loading: 'Processing...',
      save: 'Save Changes',
      saved: 'Saved Successfully',
      error: 'An error occurred',
      success: 'Operation completed successfully',
    },
  },
  fa: {
    dir: 'rtl',
    name: 'فارسی',
    nativeName: 'فارسی (ایران)',
    flag: '🇮🇷',
    nav: {
      articles: 'مقالات و اخبار',
      volunteerForm: 'فرم همکاری داوطلبان',
      contactUs: 'تماس با ما',
      login: 'ورود به حساب',
      logout: 'خروج از حساب',
      admin: 'پنل مدیریت',
      home: 'صفحه اصلی',
    },
    hero: {
      badge: 'درباره ما | روایت شکل‌گیری کمپین سبز',
      title: 'کمپین سبز: از یک ایده تا حرکتی منطقه‌ای برای زمین پاک',
      subtitle: 'ما یک شبکه مردمی و داوطلبانه محیط‌زیستی هستیم با رسالتی فراتر از مرزهای جغرافیایی؛ بدون تشریفات اداری پیچیده، به شهروندان علاقه‌مند قدرت می‌دهیم تا سفیر پاکیزگی و حامی طبیعت در شهرهای خود باشند.',
      ctaPrimary: 'پیوستن به عنوان نماینده شهر',
      ctaSecondary: 'مشاهده شهرهای فعال',
      stats: {
        participation: 'مشارکت داوطلبان',
        participationSub: 'فعالان همراه کمپین',
        hours: 'ساعات فعالیت سبز',
        hoursSub: 'در پایگاه‌های منطقه‌ای',
        volunteers: 'پایگاه‌های شهری',
        volunteersSub: 'شهر فعال در سراسر منطقه',
      },
      aboutTitle: 'داستان تولد یک حرکت مردمی',
      aboutQuote: 'کمپین سبز با یک پرسش ساده آغاز شد: «چرا باید منتظر بنشینیم تا دیگران شهرهایمان را پاکیزه کنند؟»',
      aboutDesc: 'ما تجمعی از داوطلبان دلسوز بودیم که از مشاهده کوه‌های زباله در طبیعت و فضاهای شهری خسته بودیم. به جای گلایه و انتظار، اولین حرکت میدانی را آغاز کردیم و خیلی زود فهمیدیم این دغدغه فراتر از یک مرز است؛ این نیاز کل منطقه و جهان است.',
      impactTitle: 'تأثیر فرامرزی و منطقه‌ای',
      impactDesc: 'همبستگی داوطلبان برای داشتن زمین و محیط زیستی پاک‌تر',
      valuesTitle: 'اصول کاری و خط قرمز ما',
      valuesDesc: 'ما مخالف بروکراسی و دیوان‌سالاری پیچیده هستیم. باور داریم هر شهر و روستا راهکارهای بومی خود را بهتر می‌شناسد.',
      pillar1Title: '۱. مستندسازی تصویری',
      pillar1Desc: 'تمامی فعالیت‌های میدانی با مستندات تصویری واقعی از داوطلبان و پاک‌سازی به صورت شفاف ثبت می‌شود.',
      pillar2Title: '۲. ثبت وزن دقیق زباله',
      pillar2Desc: 'ما وزن دقیق زباله‌های جمع‌آوری شده را بر حسب کیلوگرم اندازه‌گیری و در سیستم آمار ثبت می‌کنیم.',
      pillar3Title: '۳. مشارکت داوطلبان',
      pillar3Desc: 'ارسال آمار دقیق داوطلبان فعال جهت نمایش قدرت همبستگی مردمی در حفظ محیط زیست.',
      branchesBadge: 'مراکز فعال و توسعه پایدار',
      branchesTitle: '۵ پایگاه فعال در پیشروی جنبش سبز',
      branchesDesc: 'مسیر ما تازه آغاز شده است، اما پایه‌هایی مستحکم بنا کرده‌ایم. هم‌اکنون شبکه ما از طریق ۵ کانون فعال استانی هدایت می‌شود و در حال آماده‌سازی برای توسعه اقلیمی است.',
      chapterMainName: 'ستاد مرکزی کمپین',
      chapterMainRole: 'مرکز مدیریت کلان',
      chapterTehranName: 'پایگاه تهران',
      chapterKarajName: 'پایگاه کرج',
      chapterMashhadName: 'پایگاه مشهد',
      chapterTabrizName: 'پایگاه تبریز',
      chapterRoleActive: 'شاخه فعال استانی',
      chapterIntName: 'توسعه بین‌الملل',
      chapterIntRole: 'به زودی در منطقه',
      joinTitle: 'آیا شهر شما نیازمند نماینده محیط زیست است؟',
      joinDesc: 'ما به اینجا رسیدیم چون افراد باانگیزه گفتند: «من می‌توانم تفاوت ایجاد کنم.» برای نماینده شدن فقط به عشق و انگیزه نیاز دارید، نه هیچ سرمایه مالی.',
      joinCityPlaceholder: 'نام شهر شما',
      joinFooterNotice: 'با هم ثابت کنیم حفظ محیط زیست مرز نمی‌شناسد. 🌱',
    },
    footer: {
      greetingMorning: 'صبح بخیر، حامی و مدافع زمین پاک!',
      greetingAfternoon: 'روز بخیر، دوستدار و سفیر طبیعت!',
      greetingEvening: 'عصر بخیر، همراه صمیمی سیاره سبز!',
      description: 'جنبشی مستقل، مردمی و بین‌المللی با تمرکز بر حفظ محیط زیست، ترویج سبک زندگی پایدار و حمایت از اکوسیستم‌های منطقه.',
      quickLinks: 'دسترسی‌های سریع سیستم',
      newsletterTitle: 'عضویت در خبرنامه تحلیلی',
      newsletterDesc: 'به جمع هزاران کنشگر سبز بپوندید و گزارش‌های دوهفتگی میدانی و مقالات آموزشی را مستقیماً دریافت کنید.',
      emailPlaceholder: 'آدرس ایمیل خود را وارد کنید...',
      subscribeBtn: 'عضویت در خبرنامه',
      copyright: 'تمامی حقوق متعلق به کمپین سبز می‌باشد.',
      brandName: 'کمپین سبز',
      joinCommunity: 'همراه ما باشید',
      engineeredBy: 'توسعه و معماری وب توسط',
      samanic: 'تيم سامانیک',
    },
    contactUs: {
      title: 'ارتباط مستقیم با تیم کمپین سبز',
      subtitle: 'پیشنهادات، ایده‌های نوآورانه یا درخواست همکاری رسانه‌ای دارید؟ ما با افتخار پذیرای پیام شما هستیم.',
      formTitle: 'ارسال پیام مستقیم',
      nameLabel: 'نام و نام خانوادگی',
      namePlaceholder: 'علی محمدی',
      emailLabel: 'آدرس ایمیل معتبر',
      emailPlaceholder: 'info@example.ir',
      msgLabel: 'متن پیام یا طرح پیشنهادی',
      msgPlaceholder: 'ایده‌ها یا چگونگی همکاری خود با کمپین سبز را با ما در میان بگذارید...',
      sendBtn: 'ارسال پیام به تیم',
      sending: 'در حال ارسال پیام...',
      successMsg: 'پیام شما با موفقیت در سیستم دریافت شد و به دست کارگروه هماهنگی رسید!',
      errorMsg: 'ارسال پیام با خطا مواجه شد؛ لطفاً وضعیت اتصال اینترنت خود را بررسی و مجدداً تلاش کنید.',
      socialTitle: 'شبکه‌های اجتماعی و کانال‌های اطلاع‌رسانی',
    },
    articles: {
      title: 'مقالات، اخبار و گزارش‌های میدانی',
      subtitle: 'تازه‌ترین رویدادهای محیط‌زیستی، راهنماهای کاربردی زندگی پایدار و روایت‌های الهام‌بخش داوطلبان.',
      searchPlaceholder: 'جستجوی مقالات بر اساس عنوان یا کلیدواژه‌ها...',
      readMore: 'مطالعه کامل مقاله',
      minRead: 'دقیقه مطالعه',
      noArticles: 'هیچ مقاله‌ای با این مشخصات یا عبارت جستجو یافت نشد!',
      noBookmarks: 'شما هنوز مقاله‌ای را در فهرست نشان‌شده‌های شخصی خود ذخیره نکرده‌اید.',
      author: 'نویسنده',
      publishedOn: 'تاریخ انتشار',
      listenVoice: 'پخش روخوانی صوتی مقاله',
      playingVoice: 'در حال پخش صدای نویسنده...',
      voiceGuidance: 'امکان ویژه صوتی: می‌توانید با کلیک روی دکمه پخش در نوار بالا، به خوانش مستقیم مقاله توسط نویسنده گوش دهید!',
      commentsTitle: 'دیدگاه‌ها و گفتگوهای کاربران',
      writeComment: 'نظرات یا تجربیات خود را در مورد این مبحث بنویسید...',
      submitComment: 'ارسال دیدگاه',
      commentPendingNotice: 'دیدگاه ارزشمند شما دریافت شد و پس از بررسی و تایید ناظر سایت به نمایش درمی‌آید.',
      like: 'پسندیدن',
      bookmark: 'نشان‌کردن',
      saved: 'ذخیره شد',
      share: 'اشتراک‌گذاری لینک',
      copied: 'لینک کپی شد!',
      allTab: 'همه مقالات',
      bookmarksTab: 'ذخیره‌شده‌های من',
      clearFilters: 'حذف فیلترها و جستجو',
      anonymous: 'کاربر ناشناس',
      noCommentsYet: 'هنوز دیدگاهی برای این مطلب ثبت نشده است!',
      beFirstComment: 'شما اولین نفری باشید که نظر خود را پیرامون این موضوع به اشتراک می‌گذارد.',
      editorialTeam: 'تیم مدیریت کمپین سبز',
      langFilterAll: 'همه زبان‌ها',
      editArticle: 'ویرایش مقاله',
      deleteArticle: 'حذف مقاله',
      confirmDeleteTitle: 'حذف مقاله؟',
      confirmDeleteMsg: 'آیا از حذف این مقاله اطمینان دارید؟ این عملیات قابل بازگشت نیست.',
      deleteSuccess: 'مقاله با موفقیت حذف شد.',
      writtenIn: 'نوشته شده به زبان',
    },
    createArticle: {
      pageTitle: 'انتشار مقاله و گزارش جدید',
      titleLabel: 'عنوان اصلی مقاله',
      titlePlaceholder: 'عنوانی جذاب و مرتبط با اهداف کمپین وارد کنید...',
      authorLabel: 'نام نویسنده یا گروه نماینده',
      authorPlaceholder: 'مثال: علیرضا - نماینده کارگروه شمال...',
      contentLabel: 'متن اصلی و بدنه گزارش',
      coverUploadLabel: 'تصویر شاخص مقاله',
      voiceUploadLabel: 'خوانش صوتی مقاله (اختیاری و ویژه)',
      voiceExplanation: 'مستقیماً از طریق مرورگر صدای روخوانی خود از این مقاله را ضبط کنید یا یک فایل MP3 آپلود کنید تا کاربران بتوانند در زمان مطالعه به صدای شما گوش دهند.',
      submitBtn: 'انتشار رسمی مقاله',
      publishing: 'در حال ذخیره در پایگاه داده...',
      editMode: 'حالت ویرایشگر',
      previewMode: 'پیش‌نمایش زنده',
      startRecord: 'شروع ضبط صدای روخوانی',
      stopRecord: 'توقف ضبط صدا',
      uploadMp3: 'آپلود فایل صوتی MP3',
      removeAudio: 'حذف و آپلود مجدد',
      audioReady: 'فایل صوتی ضمیمه شد!',
      playingAudio: 'در حال پخش پیش‌نمایش صدا...',
      livePreviewBanner: 'نمای پیش‌نمایش زنده مقاله پیش از انتشار عمومی در سایت',
      untitled: 'پیش‌نویس مقاله بدون عنوان',
      orText: 'یا',
      micDeniedTitle: 'دسترسی میکروفون رد شد',
      micDeniedDesc: 'لطفاً دسترسی میکروفون را در مرورگر مجاز کنید.',
      audioAttachedTitle: 'فایل صوتی افزوده شد',
      audioAttachedDesc: 'فایل صوتی شما با موفقیت به مقاله ضمیمه شد.',
      uploadErrorTitle: 'خطا در بارگذاری',
      uploadErrorDesc: 'پردازش فایل صوتی با شکست مواجه شد.',
      missingDataTitle: 'اطلاعات ناقص است',
      missingDataDesc: 'لطفاً قبل از انتشار، عنوان و محتوا را وارد کنید.',
      publishedTitle: 'منتشر شد!',
      publishedDesc: 'مقاله شما با موفقیت در پایگاه داده ثبت شد.',
      publishingFailedTitle: 'خطا در انتشار',
      articleLanguage: 'زبان مقاله',
      selectLanguageDesc: 'زبانی که این مقاله به آن نگاشته شده است را انتخاب کنید:',
      editTitle: 'ویرایش مقاله',
      updateBtn: 'ذخیره و بروزرسانی مقاله',
      updatedTitle: 'با موفقیت بروز رسانی شد!',
      updatedDesc: 'تغییرات شما با موفقیت در پایگاه داده ذخیره شد.',
    },
    volunteer: {
      title: 'فرم ثبت‌نام نمایندگان و داوطلبان شهری',
      subtitle: 'قدم در این مسیر بگذارید و رهبری برنامه‌های پاکسازی، آموزش و آگاهی‌بخش را در شهر خود بر عهده بگیرید.',
      nameLabel: 'نام و نام خانوادگی',
      countryLabel: 'کشور محل سکونت',
      cityLabel: 'شهر یا منطقه سکونت',
      selectCountry: 'انتخاب کشور',
      selectCity: 'انتخاب شهر / شهرستان',
      platformLabel: 'روش ترجیحی ارتباطی جهت هماهنگی',
      contactIdPlaceholder: 'شناسه کاربری یا شماره تماس شما در',
      platformSelectGuide: 'پلتفرم پیام‌رسان دلخواه خود را از آیکون‌های سمت چپ انتخاب کنید 👈',
      submitBtn: 'ثبت نهایی درخواست همکاری',
      successTitle: 'به خانواده کمپین سبز خوش آمدید!',
      successMsg: 'فرم اعلام آمادگی شما با موفقیت ثبت و در کارتابل هماهنگی شبکه‌ای قرار گرفت!',
      stepGeneral: 'اطلاعات فردی',
      stepExperience: 'سوابق و علاقه‌مندی‌ها',
      stepMotivation: 'انگیزه و اهداف',
      incompleteFormTitle: 'فرم ناقص است',
      incompleteFormDesc: 'لطفاً تمامی فیلدهای الزامی از جمله روش ارتباطی را پر کنید.',
    },
    notFound: {
      title: '۴۰۴ - صفحه مورد نظر پیدا نشد',
      subtitle: 'وارد مسیر نامشخصی شده‌ایم',
      desc: 'صفحه یا فایلی که به دنبال آن هستید ممکن است جابه‌جا شده، تغییر نام یافته یا دیگر در سامانه وجود نداشته باشد.',
      homeBtn: 'بازگشت به صفحه اصلی سایت',
    },
    admin: {
      title: 'پنل مدیریت یکپارچه',
      subtitle: 'مرکز آمار لحظه‌ای، نظارت بر مقالات و مدیریت تایید دیدگاه‌ها',
      newArticleBtn: 'ایجاد مقاله جدید',
      totalViews: 'تعداد کل بازدیدها',
      totalArticles: 'مقالات منتشرشده',
      pendingComments: 'نظرات نیازمند بررسی',
      topAuthor: 'برترین کارگروه / نویسنده',
      mostViewed: 'مقاله‌ پربازدید سیستم',
      moderateComments: 'صف بررسی و تایید نظرات',
      volunteersList: 'فهرست داوطلبان و نمایندگان',
      approve: 'تایید و انتشار',
      delete: 'حذف از پایگاه داده',
      noData: 'موردی برای نمایش در سیستم وجود ندارد.',
      unknownAuthor: 'تیم مدیریت کمپین',
      pendingTag: 'در انتظار تایید',
      onArticle: 'در مقاله:',
      platform: 'پلتفرم:',
      registeredOn: 'تاریخ عضویت:',
      realViewCounter: 'آمار بازدید واقعی:',
      commentApprovedTitle: 'تایید نظر',
      commentApprovedDesc: 'نظر کاربر با موفقیت تایید و منتشر شد.',
      deleteConfirm: 'آیا از حذف این نظر مطمئن هستید؟',
      commentDeletedTitle: 'حذف نظر',
      commentDeletedDesc: 'نظر مورد نظر با موفقیت از سیستم حذف شد.',
    },
    login: {
      title: 'ورود به پنل مدیریت',
      subtitle: 'جهت دسترسی به تنظیمات و کارتابل ادمین، اعتبارنامه خود را وارد کنید',
      emailLabel: 'آدرس ایمیل مدیریت',
      passwordLabel: 'رمز عبور امنیتی',
      submitBtn: 'بررسی و ورود به پنل',
      checking: 'در حال احراز هویت...',
      errorMsg: 'احراز هویت ناموفق: ایمیل یا رمز عبور اشتباه است.',
      capsLockAlert: 'هشدار: کلید Caps Lock کیبورد شما روشن است!',
    },
    common: {
      back: 'بازگشت',
      loading: 'در حال پردازش...',
      save: 'ذخیره تغییرات',
      saved: 'با موفقیت ذخیره شد',
      error: 'خطایی در سیستم رخ داد',
      success: 'عملیات با موفقیت انجام شد',
    },
  },
  tr: {
    dir: 'ltr',
    name: 'Türkçe',
    nativeName: 'Türkçe (TR)',
    flag: '🇹🇷',
    nav: {
      articles: 'Makaleler',
      volunteerForm: 'Gönüllü Formu',
      contactUs: 'İletişim',
      login: 'Giriş Yap',
      logout: 'Çıkış Yap',
      admin: 'Yönetim Paneli',
      home: 'Ana Sayfa',
    },
    hero: {
      badge: 'Hakkımızda | Yeşil Kampanyanın Öyküsü',
      title: 'Yeşil Kampanya: Bir Fikir den Temiz Dünya için Bölgesel Bir Harekete',
      subtitle: 'Sınırları aşan bir misyona sahip, gönüllü yönelimli uluslararası bir çevre ağıyız. Karmaşık bürokrasi olmadan, insanları kendi şehirlerinde çevre koruma elçisi olmaları için güçlendiriyoruz.',
      ctaPrimary: 'Şehir Temsilcisi Olun',
      ctaSecondary: 'Aktif Şehirleri Keşfet',
      stats: {
        participation: 'Katılımcı Sayısı',
        participationSub: 'Aktif gönüllü',
        hours: 'Gönüllülük Saatleri',
        hoursSub: 'Bölgesel merkezlerde',
        volunteers: 'Aktif Merkezler',
        volunteersSub: 'Bölge çapındaki şehirler',
      },
      aboutTitle: 'Bir Fikrin Doğuşu',
      aboutQuote: 'Yeşil Kampanya basit bir soruyla başladı: “Şehirlerimizi temizlemek için neden başkalarını bekleyelim?”',
      aboutDesc: 'Doğadaki ve kentsel alanlardaki çöp yığınlarını görmekten yorulan tutkulu bir gönüllü ekibiydik. Şikâyet etmek yerine sahaya indik. Bu ihtiyacın coğrafi sınırları aştığını ve sürdürülebilir bir gezegen için ortak bir sorumluluk olduğunu hızla fark ettik.',
      impactTitle: 'Sınırlar Ötesi Etki',
      impactDesc: 'Daha temiz ve dayanıklı bir gezegen için toplulukları birleştiriyoruz.',
      valuesTitle: 'Nasıl Çalışıyoruz ve Kırmızı Çizgimiz',
      valuesDesc: 'Karmaşık merkezi bürokrasiyi reddediyoruz. Her topluluğun kendi yerel çözümlerini en iyi şekilde bildiğine inanıyoruz.',
      pillar1Title: '1. Doğrulanmış Fotoğraflar',
      pillar1Desc: 'Her temizlik hareketi, gönüllü çalışmaların görsel kanıtlarıyla şeffaf bir şekilde belgelenir.',
      pillar2Title: '2. Kesin Atık Ağırlığı',
      pillar2Desc: 'Toplanan çevre atıklarının tam ağırlığını kilogram cinsinden ölçüp kaydediyoruz.',
      pillar3Title: '3. Gönüllü Sayısı',
      pillar3Desc: 'Gerçek zamanlı toplumsal katılımı ve topluluk gücünü gösteren şeffaf veriler.',
      branchesBadge: 'Mevcut Durum ve Büyüme',
      branchesTitle: 'Harekete Öncülük Eden 5 Aktif Merkez',
      branchesDesc: 'Yolculuğumuz henüz yeni başladı, ancak sağlam temeller attık. Şu anda ağımız, topluluklar arasında hızla genişleyen 5 aktif bölge merkezi üzerinden faaliyet gösteriyor.',
      chapterMainName: 'Ana Kampanya Merkezi',
      chapterMainRole: 'Merkezi Üs',
      chapterTehranName: 'Tahran Merkezi',
      chapterKarajName: 'Kerec Merkezi',
      chapterMashhadName: 'Meşhed Merkezi',
      chapterTabrizName: 'Tebriz Merkezi',
      chapterRoleActive: 'Aktif Bölge Merkezi',
      chapterIntName: 'Uluslararası Merkez',
      chapterIntRole: 'Yakında Bölgede',
      joinTitle: 'Şehrinizin Bir Çevre Elçisine İhtiyacı Var Mı?',
      joinDesc: 'Bu noktaya, tutkulu insanların “Fark yaratabilirim” demesiyle geldik. Şehir temsilcisi olmak için ihtiyacınız olan tek şey tutku—finansman gerekmez.',
      joinCityPlaceholder: 'Şehriniz / Bölgeniz',
      joinFooterNotice: 'Topluluk dayanışmasının sınır tanımadığını birlikte kanıtlayalım. 🌱',
    },
    footer: {
      greetingMorning: 'Günaydın, Dünyanın Koruyucusu!',
      greetingAfternoon: 'Tünaydın, Doğa Dostu!',
      greetingEvening: 'İyi Akşamlar, Yeşil Dünyanın Elçisi!',
      description: 'Temiz bir dünya, sürdürülebilir yaşam ve doğayı korumaya adamış bağımsız, vatandaş öncelikli uluslararası çevre hareketi.',
      quickLinks: 'Hızlı Erişim',
      newsletterTitle: 'Bültene Kayıt Olun',
      newsletterDesc: 'Aktif çevre elçilerine katılın, iki haftalık saha raporları ve eğitici içerikleri doğrudan e-posta ile alın.',
      emailPlaceholder: 'E-posta adresinizi girin...',
      subscribeBtn: 'Bültene Katıl',
      copyright: 'Yeşil Kampanya’nın tüm hakları saklıdır.',
      brandName: 'Yeşil Kampanya',
      joinCommunity: 'Topluluğa Katılın',
      engineeredBy: 'Web mimarisi ve geliştirme:',
      samanic: 'Samanic Ekibi',
    },
    contactUs: {
      title: 'Yeşil Kampanya ile İletişim Kurun',
      subtitle: 'Fikirleriniz, işbirliği önerileriniz veya medya talepleriniz mi var? Sizinle çalışmaktan mutluluk duyarız.',
      formTitle: 'Doğrudan Mesaj Gönder',
      nameLabel: 'Adınız ve Soyadınız',
      namePlaceholder: 'Ahmet Yılmaz',
      emailLabel: 'E-posta Adresi',
      emailPlaceholder: 'merhaba@ornek.com',
      msgLabel: 'Mesajınız veya Öneriniz',
      msgPlaceholder: 'Düşüncelerinizi veya bizimle nasıl çalışabileceğinizi paylaşın...',
      sendBtn: 'Mesaj Gönder',
      sending: 'Gönderiliyor...',
      successMsg: 'Mesajınız başarıyla küresel ekibimize iletildi!',
      errorMsg: 'Gönderim başarısız oldu. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.',
      socialTitle: 'Sosyal Medyada Bizi Takip Edin',
    },
    articles: {
      title: 'Çevre Bülteni ve Makaleler',
      subtitle: 'En güncel saha raporları, pratik sürdürülebilirlik rehberleri ve ilham verici topluluk hikayeleri.',
      searchPlaceholder: 'Başlık veya kelimelerle makale arayın...',
      readMore: 'Makaleyi Oku',
      minRead: 'dk okuma',
      noArticles: 'Aramanızla eşleşen makale bulunamadı!',
      noBookmarks: 'Henüz kaydettiğiniz bir makale yer almıyor.',
      author: 'Yazar',
      publishedOn: 'Yayın Tarihi',
      listenVoice: 'Yazar Sesli Anlatımını Dinle',
      playingVoice: 'Sesli Anlatım Çalıyor...',
      voiceGuidance: 'Sesli Anlatım Özelliği: Üstteki dinle butonuna basarak yazarın bu makaleyi sesli okuduğu yayını dinleyebilirsiniz!',
      commentsTitle: 'Yorumlar & Tartışmalar',
      writeComment: 'Düşüncelerinizi ve görüşlerinizi yazın...',
      submitComment: 'Yorumu Gönder',
      commentPendingNotice: 'Yorumunuz alındı ve denetim incelemesinin ardından yayınlanacaktır.',
      like: 'Beğen',
      bookmark: 'Kaydet',
      saved: 'Kaydedildi',
      share: 'Bağlantıyı Paylaş',
      copied: 'Bağlantı Kopyalandı!',
      allTab: 'Tüm Makaleler',
      bookmarksTab: 'Kaydettiklerim',
      clearFilters: 'Filtreleri Temizle',
      anonymous: 'Anonim Gönüllü',
      noCommentsYet: 'Henüz bu makale için yorum yapılmamış!',
      beFirstComment: 'Bu konu hakkında düşüncelerini paylaşan ilk doğa dostu sen ol.',
      editorialTeam: 'Yeşil Kampanya Editör Ekibi',
      langFilterAll: 'Tüm Diller',
      editArticle: 'Makaleyi Düzenle',
      deleteArticle: 'Makaleyi Sil',
      confirmDeleteTitle: 'Makale Silinsin mi?',
      confirmDeleteMsg: 'Bu makaleyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.',
      deleteSuccess: 'Makale başarıyla silindi.',
      writtenIn: 'Yazım Dili:',
    },
    createArticle: {
      pageTitle: 'Yeni Makale ve Rapor Yayınla',
      titleLabel: 'Makale Başlığı',
      titlePlaceholder: 'Etkileyici bir çevre manşeti yazın...',
      authorLabel: 'Yazar veya Ekip Adı',
      authorPlaceholder: 'Örn: Mehmet - İzmir Merkez Temsilcisi...',
      contentLabel: 'Makale İçerik Metni',
      coverUploadLabel: 'Kapak Görseli',
      voiceUploadLabel: 'Yazar Sesli Okuma Kaydı (İsteğe bağlı)',
      voiceExplanation: 'Tarayıcı üzerinden sesinizi kaydedin veya MP3 yükleyin; okuyucular makaleyi okurken doğrudan sesinizi dinleyebilsin.',
      submitBtn: 'Makaleyi Resmi Olarak Yayınla',
      publishing: 'Veritabanına Kaydediliyor...',
      editMode: 'Editör Modu',
      previewMode: 'Canlı Önizleme',
      startRecord: 'Mikrofon Kaydını Başlat',
      stopRecord: 'Kaydı Durdur',
      uploadMp3: 'Ses MP3 Dosyası Yükle',
      removeAudio: 'Kaydı Sil ve Yeniden Yükle',
      audioReady: 'Ses Kaydı Eklendi!',
      playingAudio: 'Önizleme Çalıyor...',
      livePreviewBanner: 'Canlı Önizleme Modu - Genel yayın öncesi görünüm',
      untitled: 'Başlıksız Taslağı',
      orText: 'VEYA',
      micDeniedTitle: 'Mikrofon İzni Reddedildi',
      micDeniedDesc: 'Lütfen tarayıcınızdan ses izinlerini etkinleştirin.',
      audioAttachedTitle: 'Ses Dosyası Eklendi!',
      audioAttachedDesc: 'Sesli anlatımınız makaleyle başarıyla eşleştirildi.',
      uploadErrorTitle: 'Yükleme Hatası',
      uploadErrorDesc: 'Ses dosyası işlenirken hata oluştu.',
      missingDataTitle: 'Eksik Bilgi',
      missingDataDesc: 'Yayınlamadan önce lütfen başlık ve içerik ekleyin.',
      publishedTitle: 'Yayınlandı!',
      publishedDesc: 'Makaleniz veritabanına başarıyla kaydedildi.',
      publishingFailedTitle: 'Yayınlama Hatası',
      articleLanguage: 'Makale Dili',
      selectLanguageDesc: 'Bu makalenin yazıldığı dili seçin:',
      editTitle: 'Makaleyi Düzenle',
      updateBtn: 'Değişiklikleri Kaydet',
      updatedTitle: 'Başarıyla Güncellendi!',
      updatedDesc: 'Değişiklikleriniz veritabanına kaydedildi.',
    },
    volunteer: {
      title: 'Şehir Temsilcisi Gönüllü Formu',
      subtitle: 'Öne çıkın ve kendi şehrinizde temizlik, farkındalık ve doğa organizasyonlarına öncülük edin.',
      nameLabel: 'Ad Soyad',
      countryLabel: 'Yaşadığınız Ülke',
      cityLabel: 'Şehir veya Bölge',
      selectCountry: 'Ülke Seçin',
      selectCity: 'Şehir / İlçe Seçin',
      platformLabel: 'Tercih Edilen İletişim Platformu',
      contactIdPlaceholder: 'Kullanıcı adı veya numaranızı yazın:',
      platformSelectGuide: 'Soldaki ikonlardan tercih ettiğiniz mesajlaşma uygulamasını seçin 👈',
      submitBtn: 'Gönüllü Başvurusunu Tamamla',
      successTitle: 'Yeşil Harekete Hoş Geldiniz!',
      successMsg: 'Başvurunuz başarıyla sistem portföyüne alındı ve ekibimize iletildi!',
      stepGeneral: 'Kişisel Bilgiler',
      stepExperience: 'Deneyim ve İlgi Alanları',
      stepMotivation: 'Motivasyon Mektubu',
      incompleteFormTitle: 'Eksik Form',
      incompleteFormDesc: 'Lütfen göndermeden önce iletişim kanalı dahil tüm zorunlu alanları doldurun.',
    },
    notFound: {
      title: '404 - Sayfa Bulunamadı',
      subtitle: 'Bilinmeyen bir yola sapmış gibi görünüyoruz',
      desc: 'Aradığınız sayfa taşınmış, adı değiştirilmiş veya dijital ekosistemimizde artık yer almıyor olabilir.',
      homeBtn: 'Ana Sayfaya Dön',
    },
    admin: {
      title: 'Yönetici Kontrol Paneli',
      subtitle: 'Gerçek zamanlı istatistikler ve entegre yorum yönetim merkezi',
      newArticleBtn: 'Yeni Makale Yaz',
      totalViews: 'Toplam Görüntüleme',
      totalArticles: 'Yayınlanan Makaleler',
      pendingComments: 'Onay Bekleyen Yorumlar',
      topAuthor: 'En Aktif Yazar',
      mostViewed: 'En Çok Okunan Makale',
      moderateComments: 'Yorum Denetim Listesi',
      volunteersList: 'Kayıtlı Gönüllüler ve Temsilciler',
      approve: 'Onayla ve Yayınla',
      delete: 'Veritabanından Sil',
      noData: 'Sistemde listelenecek kayıt bulunmuyor.',
      unknownAuthor: 'Yeşil Kampanya Yönetimi',
      pendingTag: 'Onay Bekliyor',
      onArticle: 'Makalede:',
      platform: 'Platform:',
      registeredOn: 'Kayıt:',
      realViewCounter: 'Gerçek Görüntüleme:',
      commentApprovedTitle: 'Yorum Onaylandı',
      commentApprovedDesc: 'Yorum başarıyla halka açık olarak yayınlandı.',
      deleteConfirm: 'Bu yorumu silmek istediğinizden emin misiniz?',
      commentDeletedTitle: 'Yorum Silindi',
      commentDeletedDesc: 'Yorum sistemden başarıyla kaldırıldı.',
    },
    login: {
      title: 'Yönetim Portalına Giriş',
      subtitle: 'Yönetim ve paneller kütüphanesine erişmek için kimlik bilgilerinizi girin',
      emailLabel: 'Yönetici E-postası',
      passwordLabel: 'Güvenlik Şifresi',
      submitBtn: 'Kimliği Doğrula ve Giriş Yap',
      checking: 'Kimlik doğrulanıyor...',
      errorMsg: 'Kimlik doğrulama başarısız: Geçersiz e-posta veya şifre.',
      capsLockAlert: 'Uyarı: Klavyenizde Caps Lock tuşu açık!',
    },
    common: {
      back: 'Geri',
      loading: 'İşleniyor...',
      save: 'Değişiklikleri Kaydet',
      saved: 'Başarıyla Kaydedildi',
      error: 'Beklenmeyen bir sistem hatası oluştu',
      success: 'İşlem başarıyla tamamlandı',
    },
  },
  ar: {
    dir: 'rtl',
    name: 'العربية',
    nativeName: 'العربية (AR)',
    flag: '🇸🇦',
    nav: {
      articles: 'المقالات',
      volunteerForm: 'استمارة التطوع',
      contactUs: 'اتصل بنا',
      login: 'تسجيل الدخول',
      logout: 'تسجيل الخروج',
      admin: 'لوحة التحكم',
      home: 'الرئيسية',
    },
    hero: {
      badge: 'من نحن | قصة الحملة الخضراء',
      title: 'الحملة الخضراء: من فكرة إلى حركة إقليمية من أجل أرض نظيفة',
      subtitle: 'نحن شبكة بيئية دولية تطوعية بمهمة تتجاوز الحدود. نمكن الناس من أن يصبحوا سفراء لحماية البيئة في مدنهم دون تعقيدات بيروقراطية.',
      ctaPrimary: 'كن ممثلاً لمدينتك',
      ctaSecondary: 'اكتشف المدن النشطة',
      stats: {
        participation: 'آلات ومعدات مساعدة',
        participationSub: 'متطوع نشط',
        hours: 'ساعات العمل البيئي',
        hoursSub: 'في مراكزنا الإقليمية',
        volunteers: 'المراكز النشطة',
        volunteersSub: 'مدينة في جميع أنحاء المنطقة',
      },
      aboutTitle: 'ولادة فكرة ملهمة',
      aboutQuote: 'بدأت الحملة الخضراء بسؤال بسيط: «لماذا ننتظر الآخرين لتنظيف مدننا وبلداتنا؟»',
      aboutDesc: 'كنا مجموعة من المتطوعين المتحمسين الذين سئموا من رؤية أكوام النفايات في الطبيعة والساحات الحضرية. بدلاً من الشكوى والتذمر، نزلنا للعمل الميداني. وأدركنا سريعًا أن هذه الحاجة تتجاوز الحدود الجغرافية، فهي مسؤولية مشتركة من أجل كوكب مستدام.',
      impactTitle: 'تأثير إقليمي يتجاوز الحدود',
      impactDesc: 'توحيد الجهود المجتمعية في كل مكان من أجل كوكب أكثر نظافة واستدامة.',
      valuesTitle: 'كيف نعمل ومبادئنا الأساسية',
      valuesDesc: 'نحن نرفض البيروقراطية المعقدة. نؤمن بأن كل مجتمع محلي يعرف الحلول الأنسب لبيئته.',
      pillar1Title: '1. توثيق مرئي حقيقي',
      pillar1Desc: 'يتم تدوين جميع حملات التنظيف بمستندات وصور شفافة لعمل المتطوعين الميداني.',
      pillar2Title: '2. وزن النفايات بدقة',
      pillar2Desc: 'نحن نقوم بقياس وتسجيل الوزن الدقيق للنفايات المجمعة بالكيلوغرام في قاعدة بياناتنا.',
      pillar3Title: '3. أرقام المتطوعين',
      pillar3Desc: 'إحصائيات واضحة ومحدثة تظهر قوة المشاركة المجتمعية الحقيقية والتكاتف الأهلية.',
      branchesBadge: 'الوضع الحالي والنمو',
      branchesTitle: '5 مراكز فعالة تقود الحركة البيئية',
      branchesDesc: 'رحلتنا بدأت للتو، لكننا أرسينا قواعد متينة. في الوقت الحالي، تعمل شبكتنا من خلال 5 فروع إقليمية نشطة، وتتوسع بسرعة عبر مختلف المدن والمناطق.',
      chapterMainName: 'المقر المركزي للحملة',
      chapterMainRole: 'مركز الإدارة القيادي',
      chapterTehranName: 'فرع طهران',
      chapterKarajName: 'فرع كرج',
      chapterMashhadName: 'فرع مشهد',
      chapterTabrizName: 'فرع تبريز',
      chapterRoleActive: 'فرع إقليمي نشط',
      chapterIntName: 'المركز الدولي',
      chapterIntRole: 'قريبًا في المنطقة',
      joinTitle: 'هل تحتاج مدينتك إلى سفير للبيئة والنظافة؟',
      joinDesc: 'لقد وصلنا إلى هنا لأن أناسًا شغوفين قالوا: «يمكنني إحداث فرق الحقيقي». لكي تصبح ممثلًا لمدينتك، كل ما تحتاجه هو الشغف والالتزام—دون حاجة لتمويل مالي.',
      joinCityPlaceholder: 'اسم مدينتك / منطقتك',
      joinFooterNotice: 'لنثبت معًا أن تكاتف المجتمع وعمل الخير لا يَعرف حدودًا. 🌱',
    },
    footer: {
      greetingMorning: 'صباح الخير، حامي الأرض والبيئة!',
      greetingAfternoon: 'طاب يومك، نصير الطبيعة!',
      greetingEvening: 'مساء الخير، سفير الكوكب الأخضر!',
      description: 'حركة تطوعية ومستقلة وإقليمية تهدف إلى بيئة نظيفة، ونمط حياة مستدام، وحماية موارد الطبيعة دون تعقيد.',
      quickLinks: 'الوصول السريع للنظام',
      newsletterTitle: 'اشترك في نشرتنا التفاعلية',
      newsletterDesc: 'انضم إلى آلاف النشطاء البيئيين واحصل على تقارير ميدانية ومقالات تعليمية مباشرة في بريدك.',
      emailPlaceholder: 'أدخل عنوان بريدك الإلكتروني...',
      subscribeBtn: 'الانضمام للنشرة',
      copyright: 'جميع الحقوق محفوظة للحملة الخضراء العالمية.',
      brandName: 'الحملة الخضراء',
      joinCommunity: 'انضم إلى مجتمعنا',
      engineeredBy: 'تطوير وهندسة الويب بواسطة',
      samanic: 'فريق سامانيك',
    },
    contactUs: {
      title: 'تواصل مع فريقنا أو شارك في القيادة',
      subtitle: 'نرحب باقتراحاتكم للشراكة، والتقارير الميدانية، والأسئلة العامة حول الانضمام إلينا',
      formTitle: 'إرسال رسالة مباشرة للفريق',
      nameLabel: 'الاسم الكامل أو اللقب',
      namePlaceholder: 'أحمد الإدريسي',
      emailLabel: 'عنوان البريد الإلكتروني الرسمي',
      emailPlaceholder: 'example@green-campaign.com',
      msgLabel: 'نص رسالتك أو تقريرك الميداني',
      msgPlaceholder: 'يرجى تقديم تفاصيل موقع أو استفسار الشراكة هنا...',
      sendBtn: 'إرسال الرسالة إلى غرفة قيادة الحملة',
      sending: 'جارٍ نقل بياناتك البيئية...',
      successMsg: 'تم الاستلام بنجاح! سيقوم فريق المبادرة الخضراء بمراجعة رسالتك والرد في أقرب وقت.',
      errorMsg: 'حدث خطأ في الإتصال بالخادم. يرجى التحقق من اتصال الإنترنت أو المحاولة لاحقًا.',
      socialTitle: 'تابعنا عبر القنوات والإشعارات',
    },
    articles: {
      title: 'تقارير بيئية وأبحاث الحملة',
      subtitle: 'آخر التحليلات الميدانية وجداول حملات التنظيف ومبادرات الغابات الإقليمية',
      searchPlaceholder: 'البحث عن المقالات أو تقارير التنظيف الميدانية أو الكلمات الرئيسية...',
      readMore: 'قراءة التقرير والتحقيق الكامل',
      minRead: 'دقائق للقراءة',
      noArticles: 'لم يتم العثور على مقالات تطابق البحث أو مرشحات الحفظ في الوقت الحالي',
      noBookmarks: 'لم تقم بحفظ أي مقالات في قائمة المحفوظات الخاصة بك حتى الآن.',
      author: 'بقلم التحرير:',
      publishedOn: 'تاريخ النشر في النظام:',
      listenVoice: 'الاستماع إلى القراءة الصوتية',
      playingVoice: 'جارٍ الاستماع للصوت...',
      voiceGuidance: 'تتضمن هذه المقالة قراءة صوتية مسجلة للراوي من أجل راحة الاستماع.',
      commentsTitle: 'المناقشات المجتمعية وتعليقات الأعضاء',
      writeComment: 'اكتب وجهة نظرك أو تجربتك مع مبادرات التنظيف هنا...',
      submitComment: 'إرسال ونشر تعليق للمجتمع',
      commentPendingNotice: 'تم إرسال تعليقك وسيُعرض علنًا فور التحقق منه بواسطة فريق المشهد',
      like: 'إعجاب بالتقرير البيئي',
      bookmark: 'حفظ المقال للقراءة لاحقًا',
      saved: 'تم الحفظ في المحفوظات',
      share: 'مشاركة المقال في شبكتك',
      copied: 'تم نسخ الرابط إلى الحافظة!',
      allTab: 'جميع المقالات',
      bookmarksTab: 'المقالات المحفوظة',
      clearFilters: 'مسح مرشحات البحث',
      anonymous: 'مستخدم مجهول',
      noCommentsYet: 'لم يتم العثور على مناقشات حتى الآن!',
      beFirstComment: 'كن أول عضو مجتمعي يترك تعليقًا ويبدأ النقاش.',
      editorialTeam: 'فريق تحرير الحملة الخضراء',
      langFilterAll: 'جميع اللغات',
      editArticle: 'تعديل المقال',
      deleteArticle: 'حذف المقال',
      confirmDeleteTitle: 'حذف المقال؟',
      confirmDeleteMsg: 'هل أنت متأكد من أنك تريد حذف هذا المقال؟ لا يمكن التراجع عن هذا الإجراء.',
      deleteSuccess: 'تم حذف المقال بنجاح.',
      writtenIn: 'مكتوب بلغة',
    },
    createArticle: {
      pageTitle: 'نشر مقال بيئي جديد أو تقرير حملة',
      titleLabel: 'عنوان المقال الميداني',
      titlePlaceholder: 'مثال: نتائج حملة تنظيف غابة غلستان وإعادة تأهيل التربة...',
      authorLabel: 'اسم المؤلف أو الفرع التنفيذي للحملة',
      authorPlaceholder: 'مثال: لجنة أصفهان البيئية...',
      contentLabel: 'نص التقرير أو المقالة المفصلة',
      coverUploadLabel: 'صورة الغلاف (أو اختر من المعرض الافتراضي)',
      voiceUploadLabel: 'تسجيل أو تحميل القراءة الصوتية للمقال',
      voiceExplanation: 'يمكنك استخدام الميكروفون لتسجيل صوت المقالة، أو استكشاف وتحميل ملف بودكاست/وعد مسجل مسبقًا بصيغة MP3 للمشترين.',
      submitBtn: 'حفظ ونشر في المحتوى العام',
      publishing: 'جارٍ المعالجة والنشر في الخادم...',
      editMode: 'وضع التحرير',
      previewMode: 'معاينة حية',
      startRecord: 'بدء تسجل صوت القراءة',
      stopRecord: 'إيقاف التسجيل',
      uploadMp3: 'تحميل ملف صوتي MP3',
      removeAudio: 'إزالة الصوت والبدء من جديد',
      audioReady: 'الملف الصوتي جاهز للمقال!',
      playingAudio: 'جارٍ تشغيل السرد...',
      livePreviewBanner: 'معاينة حية للمقال قبل النشر العام',
      untitled: 'مقال بدون عنوان',
      orText: 'أو',
      micDeniedTitle: 'تم رفض إذن الميكروفون',
      micDeniedDesc: 'يرجى تفعيل صلاحية الميكروفون في متصفحك.',
      audioAttachedTitle: 'تم إرفاق الملف الصوتي!',
      audioAttachedDesc: 'تم ربط التسجيل الصوتي بنجاح مع المقال.',
      uploadErrorTitle: 'خطا في التحميل',
      uploadErrorDesc: 'فشل في معالجة الملف الصوتي.',
      missingDataTitle: 'البيانات ناقصة',
      missingDataDesc: 'يرجى كتابة العنوان والمحتوى قبل النشر.',
      publishedTitle: 'تم النشر!',
      publishedDesc: 'تم حفظ ونشر المقال في قاعدة البيانات بنجاح.',
      publishingFailedTitle: 'خطأ في عملية النشر',
      articleLanguage: 'لغة المقال',
      selectLanguageDesc: 'اختر اللغة التي كُتب بها هذا المقال (إنجليزي، فارسي، تركي أو عربي):',
      editTitle: 'تعديل المقال',
      updateBtn: 'حفظ وتحديث المقال',
      updatedTitle: 'تم التحديث بنجاح!',
      updatedDesc: 'تم حفظ تعديلاتك في قاعدة البيانات بنجاح.',
    },
    volunteer: {
      title: 'انضم إلى حركة المندوبين والمتطوعين في مدينتك',
      subtitle: 'دون قيود بيروقراطية معقدة، سجل اسمك واصنع فجأة حية لحماية البيئة المحلية',
      nameLabel: 'الاسم الكامل أو اسم الناشط',
      countryLabel: 'البلد الإقليمي والمكان الحالي',
      cityLabel: 'المدينة المقيمة للمهمة والتنظيف',
      selectCountry: 'اختر البلد من القائمة الموحدة',
      selectCity: 'اختر أو ابحث عن مدينتك النشطة',
      platformLabel: 'طريقة وقناة التنسيق والاتصال المفضلة',
      contactIdPlaceholder: 'أدخل معرف حسابك الحاسم أو رقم هاتفك...',
      platformSelectGuide: 'اختر منصة التنسيق والاتصال المفضلة من الرموز على اليسار 👈👇',
      submitBtn: 'إرسال استمارة الانضمام وبدء التقديم',
      successTitle: 'تم التقديم بنجاح!',
      successMsg: 'تم تسجيل طلبك بنجاح. سنقوم بالرد عليك قريباً للانضمام للجنة مدينتك.',
      stepGeneral: 'البيانات الشخصية والإقامة',
      stepExperience: 'قنوات التواصل والتطبيق المفضل',
      stepMotivation: 'تأكيد الالتزام بقيم الحملة الخضراء',
      incompleteFormTitle: 'استمارة ناقصة',
      incompleteFormDesc: 'يرجى إكمال جميع الحقول المطلوبة بما في ذلك وسيلة التواصل قبل التقديم.',
    },
    notFound: {
      title: '404 - الصفحة غير موجودة في الغابة',
      subtitle: 'يبدو أنك سلكت طريقًا غير ممهد في النظام البيئي',
      desc: 'الصفحة التي تبحث عنها ربما تم نقلها أو إعادة تسميتها أو لم تعد مدرجة ضمن فروع حملتنا الخضراء النشطة.',
      homeBtn: 'العودة إلى القاعة الرئيسية للحملة',
    },
    admin: {
      title: 'لوحة التحكم المركزية للمشرفين',
      subtitle: 'مراقبة الإحصائيات في الوقت الفعلي وإدارة محتوى التعليقات والمندوبين',
      newArticleBtn: 'كتابة مقال أو تقرير جديد',
      totalViews: 'إجمالي مشاهدات المقالات',
      totalArticles: 'إجمالي المقالات والتقارير',
      pendingComments: 'تعليقات في انتظار المراجعة',
      topAuthor: 'المؤلف والكاتب الأكثر نشاطًا',
      mostViewed: 'المقال الأكثر مشاهدة في الحملة',
      moderateComments: 'مركز تدقيق ومراجعة التعليقات',
      volunteersList: 'سجل المتطوعين والمندوبين الميدانيين',
      approve: 'الموافقة والنشر العلني',
      delete: 'الحذف نهائيًا من النظام',
      noData: 'لا توجد سجلات لعرضها في الوقت الحاضر.',
      unknownAuthor: 'إدارة الحملة الخضراء',
      pendingTag: 'قيد الانتظار',
      onArticle: 'في المقال:',
      platform: 'المنصة:',
      registeredOn: 'تاريخ التسجيل:',
      realViewCounter: 'إحصاءات المشاهدات الحقيقية:',
      commentApprovedTitle: 'تمت الموافقة على التعليق',
      commentApprovedDesc: 'تم نشر التعليق وإظهاره للعامة بنجاح.',
      deleteConfirm: 'هل أنت متأكد من رغبتك في حذف هذا التعليق؟',
      commentDeletedTitle: 'تم حذف التعليق',
      commentDeletedDesc: 'تمت إزالة التعليق من قاعدة البيانات بنجاح.',
    },
    login: {
      title: 'تسجيل الدخول إلى لوحة الإدارة',
      subtitle: 'أدخل بيانات الاعتماد الخاصة بك للوصول إلى أنظمة الإدارة والتحكم التنفيذي',
      emailLabel: 'البريد الإلكتروني للإدارة',
      passwordLabel: 'كلمة المرور الأمنية',
      submitBtn: 'التحقق وتسجيل الدخول',
      checking: 'جارٍ التحقق من الصلاحيات...',
      errorMsg: 'فشل التحقق: البريد الإلكتروني أو كلمة المرور غير صحيحة.',
      capsLockAlert: 'تنبيه: زر Caps Lock في لوحة المفاتيح في وضع التشغيل!',
    },
    common: {
      back: 'رجوع',
      loading: 'جارٍ المعالجة...',
      save: 'حفظ التعديلات',
      saved: 'تم الحفظ بنجاح',
      error: 'حدث خطأ غير متوقع',
      success: 'تم إنجاز العملية بنجاح',
    },
  },
};
